import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CalAngularService } from '@cvx/cal-angular';
import { OKTA_AUTH } from '@okta/okta-angular';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable()
export class AppInterceptor implements HttpInterceptor {
    private hasRedirectedToAccessDenied = false;
    private hasCheckedUserRoles = false;
    private userHasAppRoles = false;

    constructor(
        private authService: CalAngularService,
        @Inject(OKTA_AUTH) private oktaAuth: any,
        private router: Router
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
        // Skip auth for non-API requests (e.g., i18n translation files, assets)
        if (!req.url.startsWith(environment.baseApiUrl) && !req.url.startsWith('http')) {
            return next.handle(req);
        }

        const idp = localStorage.getItem('bq-idp');

        if (idp === 'okta') {
            return from(this.oktaAuth.tokenManager.get('accessToken')).pipe(
                switchMap((tokenObj: any) => {
                    if (tokenObj && tokenObj.accessToken) {
                        const cloned = req.clone({
                            setHeaders: { Authorization: `Bearer ${tokenObj.accessToken}` }
                        });
                        return next.handle(cloned);
                    }
                    return next.handle(req);
                }),
                catchError((error: HttpErrorResponse) => {
                    return throwError(() => error);
                })
            );
        }

        return this.authService.getAADToken([environment.apiUrl]).pipe(
            switchMap(token => {
                const cloned = req.clone({
                    setHeaders: {
                        Authorization: `Bearer ${token}`
                    }
                });
                return next.handle(cloned).pipe(
                    catchError((error: HttpErrorResponse) => {
                        if (error.status === 403) {
                            // Don't redirect if already on the request-access page
                            if (this.router.url === '/request-access') {
                                return throwError(() => error);
                            }

                            // Check user roles only once
                            if (!this.hasCheckedUserRoles) {
                                this.hasCheckedUserRoles = true;
                                this.userHasAppRoles = this.checkIfUserHasApplicationRoles();
                            }

                            // Only redirect if user has NO application roles at all
                            // If they have roles, the 403 is for a specific resource they don't have access to
                            if (!this.hasRedirectedToAccessDenied && !this.userHasAppRoles) {
                                this.hasRedirectedToAccessDenied = true;
                                console.warn('User does not have any application roles. Redirecting to request access page.');
                                this.router.navigate(['/request-access']);
                            } else if (this.userHasAppRoles) {
                                // User has roles but got 403 - this is a specific resource permission issue, not general access
                                console.warn('User has application roles but received 403 for specific resource. This is expected behavior.');
                            }
                        }
                        return throwError(() => error);
                    })
                );
            })
        );
    }

    /**
     * Checks if the user has any application-specific roles
     * @returns true if user has at least one SDD, CDD, or BQ role
     */
    private checkIfUserHasApplicationRoles(): boolean {
        const idp = localStorage.getItem('bq-idp');
        let userRoles: string[] = [];

        // For Okta users (external BQ users), we don't need to check here
        // They will have their permissions validated by the BQ guards
        if (idp === 'okta') {
            return true;
        }

        // For Azure AD users, check if they have valid roles
        userRoles = this.authService.cvxClaimsPrincipal?.roles ??
            ((JSON.parse(localStorage?.getItem('cachedCvxClaimsPrincipal') || '{}'))?._roles || []);

        // Get environment suffix
        const envName = environment.environmentName;
        let envSuffix = '';

        if (envName === 'development' || envName === 'local') {
            envSuffix = '-Dev';
        } else if (envName === 'test') {
            envSuffix = '-Test';
        }

        // Define all valid application roles (base names) - including BQ roles
        const validAppRolePatterns = [
            'CddAnalyst',
            'CddComplianceAnalyst',
            'CddLegalCompliance',
            'CddM&TCreator',
            'SddGeneralUser',
            'SddAnalyst',
            'SddCompliance',
            'BqAdministrator',
            'BqCompliance',
            'BqMT',
            'BqSCM',
            'BqExternalUser'
        ];

        // Check if user has any of these roles (with or without environment suffix)
        const hasRole = userRoles.some((userRole: string) => {
            return validAppRolePatterns.some(appRole => {
                const roleWithEnv = appRole + envSuffix;
                return userRole === appRole || userRole === roleWithEnv;
            });
        });

        if (hasRole) {
            console.log('User has valid application roles:', userRoles);
        } else {
            console.log('User does not have any valid application roles. User roles:', userRoles);
        }

        return hasRole;
    }
}
